(() => {
  'use strict';

  const cfg = window.PerformerConfig || {};
  const proxyBase = cfg.proxyBase || '/apps/performer';
  const sessionKeyName = cfg.sessionStorageKey || 'performer.session.v1';
  const browserKeyName = cfg.browserStorageKey || 'performer.browser.v1';
  const pendingTokenKeyName = cfg.pendingTokenStorageKey || 'performer.pending.token.v1';
  const handoffStorageKeyName = cfg.handoffStorageKey || 'performer.handoff.v1';
  const handoffHashKeyName = cfg.handoffHashKey || 'pf_handoff';
  const fastrrCouponParam = cfg.fastrrCouponParam || 'fastrrCouponCodeFromLink';
  const prepaidGuardStorageKeyName = cfg.prepaidGuardStorageKey || 'performer.prepaid.guard.v1';
  const sessionTtlHours = Number(cfg.sessionTtlHours || 72);
  let currentEntitlement = null;
  let currentSessionKey = null;
  let currentCorrelationToken = null;
  let cartSyncPromise = null;

  function storageGet(key) {
    try { return window.localStorage.getItem(key); } catch (_) { return null; }
  }
  function storageSet(key, value) {
    try { window.localStorage.setItem(key, value); } catch (_) {}
  }
  function storageRemove(key) {
    try { window.localStorage.removeItem(key); } catch (_) {}
  }
  function sessionGet(key) {
    try { return window.sessionStorage.getItem(key); } catch (_) { return null; }
  }
  function sessionRemove(key) {
    try { window.sessionStorage.removeItem(key); } catch (_) {}
  }
  function clearHandoffState() {
    sessionRemove(handoffStorageKeyName);
    sessionRemove(pendingTokenKeyName);
    try {
      const url = new URL(window.location.href);
      const rawHash = String(url.hash || '').replace(/^#/, '');
      if (!rawHash) return;
      const params = new URLSearchParams(rawHash);
      if (!params.has(handoffHashKeyName)) return;
      params.delete(handoffHashKeyName);
      const nextHash = params.toString();
      url.hash = nextHash ? `#${nextHash}` : '';
      replaceVisibleUrl(url);
    } catch (_) {}
  }
  function randomId() {
    if (window.crypto && typeof window.crypto.randomUUID === 'function') return window.crypto.randomUUID();
    const bytes = new Uint8Array(24);
    if (window.crypto && window.crypto.getRandomValues) window.crypto.getRandomValues(bytes);
    else for (let i = 0; i < bytes.length; i++) bytes[i] = Math.floor(Math.random() * 256);
    return Array.from(bytes, b => b.toString(16).padStart(2, '0')).join('');
  }
  function browserId() {
    let value = storageGet(browserKeyName);
    if (!value) { value = randomId(); storageSet(browserKeyName, value); }
    return value;
  }
  async function post(path, body, options = {}) {
    const response = await fetch(`${proxyBase}${path}`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body || {}),
      credentials: 'same-origin',
      cache: 'no-store',
      keepalive: Boolean(options.keepalive)
    });
    let data = {};
    try { data = await response.json(); } catch (_) {}
    return { response, data };
  }
  function replaceVisibleUrl(url) {
    const search = url.searchParams.toString();
    window.history.replaceState(
      window.history.state,
      '',
      `${url.pathname}${search ? `?${search}` : ''}${url.hash}`
    );
  }
  function cleanInvitationUrl(options = {}) {
    try {
      const url = new URL(window.location.href);
      let changed = false;

      if (options.offer !== false && url.searchParams.has(cfg.tokenParam || 'pf_offer')) {
        url.searchParams.delete(cfg.tokenParam || 'pf_offer');
        changed = true;
      }
      if (options.coupon === true && url.searchParams.has(fastrrCouponParam)) {
        url.searchParams.delete(fastrrCouponParam);
        changed = true;
      }

      if (changed) replaceVisibleUrl(url);
    } catch (_) {}
  }
  function ensureValidatedCouponUrl(entitlement) {
    try {
      const expected = entitlement && entitlement.checkout && entitlement.checkout.coupon_code
        ? String(entitlement.checkout.coupon_code)
        : '';
      if (!expected) return false;

      const url = new URL(window.location.href);
      const current = url.searchParams.get(fastrrCouponParam) || '';
      if (String(current).toUpperCase() === String(expected).toUpperCase()) return true;

      url.searchParams.set(fastrrCouponParam, expected);
      replaceVisibleUrl(url);
      return true;
    } catch (_) {
      return false;
    }
  }
  function scheduleValidatedCouponUrlCleanup(entitlement) {
    // Historical name retained for compatibility. We intentionally DO NOT
    // remove the coupon after a timer anymore. Fastrr can initialize late and
    // can also need the parameter again on product/cart navigation. Keeping the
    // unique coupon in the query string for the active private-offer session is
    // what makes checkout autofill deterministic.
    ensureValidatedCouponUrl(entitlement);
    if (document.readyState !== 'complete') {
      window.addEventListener('load', () => ensureValidatedCouponUrl(entitlement), { once: true });
    }
  }
  function performerPrepaidGuard(entitlement) {
    const offer = entitlement && entitlement.offer ? entitlement.offer : {};
    if (!entitlement || offer.prepaid_only === false) return null;
    const buyQty = positiveNumber(offer.buy_quantity, 2);
    const getQty = positiveNumber(offer.get_quantity, 2);
    const defaultCampaign = buyQty === 2 && getQty === 2
      ? 'performer_b2g2_prepaid'
      : 'performer_' + String((entitlement.campaign && entitlement.campaign.id) || 'private_offer').replace(/[^a-z0-9]+/gi, '_').toLowerCase();
    const now = Date.now();
    const ttlMs = Math.max(1, Number.isFinite(sessionTtlHours) ? sessionTtlHours : 72) * 60 * 60 * 1000;
    const campaignEnd = Date.parse(String(entitlement.ends_at || ''));
    const expiresAt = Number.isFinite(campaignEnd) && campaignEnd > now ? Math.min(campaignEnd, now + ttlMs) : now + ttlMs;
    return {
      campaign_id: String((entitlement.campaign && entitlement.campaign.id) || ''),
      prepaid_only: true,
      utm_source: String(offer.utm_source || 'whatsapp'),
      utm_medium: String(offer.utm_medium || 'engage360'),
      utm_campaign: String(offer.utm_campaign || defaultCampaign),
      coupon_code: entitlement.checkout && entitlement.checkout.coupon_code ? String(entitlement.checkout.coupon_code) : '',
      expires_at: expiresAt
    };
  }
  function storedPrepaidGuard() {
    try {
      const raw = storageGet(prepaidGuardStorageKeyName);
      if (!raw) return null;
      const guard = JSON.parse(raw);
      if (!guard || guard.prepaid_only !== true || !guard.expires_at || Number(guard.expires_at) <= Date.now()) {
        storageRemove(prepaidGuardStorageKeyName);
        return null;
      }
      return guard;
    } catch (_) {
      storageRemove(prepaidGuardStorageKeyName);
      return null;
    }
  }
  function persistPrepaidGuard(entitlement) {
    const guard = performerPrepaidGuard(entitlement);
    if (!guard) { storageRemove(prepaidGuardStorageKeyName); return null; }
    storageSet(prepaidGuardStorageKeyName, JSON.stringify(guard));
    return guard;
  }
  function enforcePrepaidGuardUrl(entitlement) {
    const guard = entitlement ? (performerPrepaidGuard(entitlement) || storedPrepaidGuard()) : storedPrepaidGuard();
    if (!guard) return false;
    try {
      const url = new URL(window.location.href);
      let changed = false;
      [['utm_source', guard.utm_source], ['utm_medium', guard.utm_medium], ['utm_campaign', guard.utm_campaign]].forEach(([key, value]) => {
        if (value && url.searchParams.get(key) !== String(value)) { url.searchParams.set(key, String(value)); changed = true; }
      });
      if (guard.coupon_code && url.searchParams.get(fastrrCouponParam) !== String(guard.coupon_code)) {
        url.searchParams.set(fastrrCouponParam, String(guard.coupon_code));
        changed = true;
      }
      if (changed) replaceVisibleUrl(url);
      document.documentElement.classList.add('performer-prepaid-guard-active');
      return true;
    } catch (_) { return false; }
  }
  function clearPrepaidGuard() {
    const guard = storedPrepaidGuard();
    storageRemove(prepaidGuardStorageKeyName);
    document.documentElement.classList.remove('performer-prepaid-guard-active');
    if (!guard) return;
    try {
      const url = new URL(window.location.href);
      let changed = false;
      [['utm_source', guard.utm_source], ['utm_medium', guard.utm_medium], ['utm_campaign', guard.utm_campaign], [fastrrCouponParam, guard.coupon_code]].forEach(([key, value]) => {
        if (value && url.searchParams.get(key) === String(value)) { url.searchParams.delete(key); changed = true; }
      });
      if (changed) replaceVisibleUrl(url);
    } catch (_) {}
  }
  function shouldForgetSession(error) {
    return ['offer_redeemed','offer_revoked','offer_expired','campaign_expired','campaign_not_active','session_expired','session_redeemed','session_revoked','session_not_found'].includes(error);
  }
  function positiveNumber(value, fallback) {
    var parsed = parseInt(value, 10);
    return Number.isFinite(parsed) && parsed > 0 ? parsed : fallback;
  }
  function renderOfferUi(data) {
    var offer = data && data.offer ? data.offer : {};
    var buyQty = positiveNumber(offer.buy_quantity, 2);
    var getQty = positiveNumber(offer.get_quantity, 2);
    var totalQty = buyQty + getQty;
    var label = 'BUY ' + buyQty + ' GET ' + getQty + ' FREE';
    var lead = 'Add any ' + totalQty + ' eligible perfumes • Pay only for ' + buyQty;

    document.documentElement.dataset.performerOfferBuy = String(buyQty);
    document.documentElement.dataset.performerOfferGet = String(getQty);
    document.documentElement.dataset.performerOfferTotal = String(totalQty);

    document.querySelectorAll('[data-performer-buy-qty]').forEach(function (el) { el.textContent = String(buyQty); });
    document.querySelectorAll('[data-performer-get-qty]').forEach(function (el) { el.textContent = String(getQty); });
    document.querySelectorAll('[data-performer-total-qty]').forEach(function (el) { el.textContent = String(totalQty); });
    document.querySelectorAll('[data-performer-offer-label]').forEach(function (el) { el.textContent = label; });
    document.querySelectorAll('[data-performer-offer-lead]').forEach(function (el) { el.textContent = lead; });
  }
  function exposeEntitlement(data) {
    currentEntitlement = data;
    window.PerformerOffer = data;
    document.documentElement.classList.remove('performer-offer-pending');
    document.documentElement.classList.add('performer-offer-active');
    document.documentElement.dataset.performerOfferType = (data.offer && data.offer.type) || '';
    document.documentElement.dataset.performerCampaign = (data.campaign && data.campaign.id) || '';
    renderOfferUi(data);
    persistPrepaidGuard(data);
    enforcePrepaidGuardUrl(data);
    window.dispatchEvent(new CustomEvent('performer:offerchange', { detail: data }));
  }
  function clearEntitlement() {
    currentEntitlement = null;
    currentSessionKey = null;
    currentCorrelationToken = null;
    window.PerformerOffer = null;
    document.documentElement.classList.remove('performer-offer-pending', 'performer-offer-active');
    delete document.documentElement.dataset.performerOfferType;
    delete document.documentElement.dataset.performerCampaign;
    delete document.documentElement.dataset.performerOfferBuy;
    delete document.documentElement.dataset.performerOfferGet;
    delete document.documentElement.dataset.performerOfferTotal;
    clearPrepaidGuard();
    window.dispatchEvent(new CustomEvent('performer:offerchange', { detail: null }));
  }
  async function syncCartAttributes() {
    if (!currentSessionKey || !currentEntitlement) return;
    if (cartSyncPromise) return cartSyncPromise;
    cartSyncPromise = fetch(`${window.Shopify && Shopify.routes ? Shopify.routes.root : '/'}cart/update.js`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
      credentials: 'same-origin',
      body: JSON.stringify({
        attributes: {
          '__performer_session': currentCorrelationToken || '',
          '__performer_campaign': currentEntitlement.campaign && currentEntitlement.campaign.id || '',
          '__performer_offer': currentEntitlement.offer && currentEntitlement.offer.type || '',
          '__performer_prepaid_only': currentEntitlement.offer && currentEntitlement.offer.prepaid_only === false ? '0' : '1',
          '__performer_utm_campaign': currentEntitlement.offer && currentEntitlement.offer.utm_campaign || 'performer_b2g2_prepaid'
        }
      })
    }).catch(() => null).finally(() => { cartSyncPromise = null; });
    return cartSyncPromise;
  }
  async function getCartToken() {
    try {
      const response = await fetch(`${window.Shopify && Shopify.routes ? Shopify.routes.root : '/'}cart.js`, { credentials: 'same-origin', cache: 'no-store' });
      const cart = await response.json();
      return cart && cart.token ? String(cart.token) : null;
    } catch (_) { return null; }
  }
  async function reportCheckoutStart() {
    if (!currentSessionKey) return;
    await syncCartAttributes();
    const cartToken = await getCartToken();
    post('/checkout-start', { session_key: currentSessionKey, cart_token: cartToken }, { keepalive: true }).catch(() => {});
  }
  async function activateFromToken(token) {
    const { response, data } = await post('/claim', { token, browser_id: browserId() });
    if (!response.ok || !data.ok) {
      if (shouldForgetSession(data.error)) {
        storageRemove(sessionKeyName);
        clearEntitlement();
        cleanInvitationUrl({ offer: true, coupon: true });
        clearHandoffState();
      }
      document.documentElement.classList.remove('performer-offer-pending');
      return { ok: false, status: response.status, error: data.error || 'claim_failed' };
    }
    currentSessionKey = data.session_key;
    currentCorrelationToken = data.correlation_token || null;
    storageSet(sessionKeyName, currentSessionKey);
    exposeEntitlement(data);
    cleanInvitationUrl({ offer: true, coupon: false });
    await syncCartAttributes();
    scheduleValidatedCouponUrlCleanup(data);
    enforcePrepaidGuardUrl(data);
    return { ok: true, status: response.status };
  }
  async function resumeSession(sessionKey) {
    const { response, data } = await post('/session', { session_key: sessionKey });
    if (!response.ok || !data.ok) {
      if (shouldForgetSession(data.error)) {
        storageRemove(sessionKeyName);
        clearEntitlement();
        clearHandoffState();
      } else {
        document.documentElement.classList.remove('performer-offer-pending');
      }
      return false;
    }
    currentSessionKey = sessionKey;
    currentCorrelationToken = data.correlation_token || null;
    exposeEntitlement(data);
    await syncCartAttributes();
    scheduleValidatedCouponUrlCleanup(data);
    enforcePrepaidGuardUrl(data);
    return true;
  }
  async function init() {
    const url = new URL(window.location.href);
    const token = url.searchParams.get(cfg.tokenParam || 'pf_offer') || sessionGet(pendingTokenKeyName);
    if (token) {
      const result = await activateFromToken(token);
      if (result && (result.ok || (result.status >= 400 && result.status < 500))) {
        sessionRemove(pendingTokenKeyName);
      }
      return;
    }
    const existing = storageGet(sessionKeyName);
    if (existing) {
      await resumeSession(existing);
    } else {
      document.documentElement.classList.remove('performer-offer-pending');
    }
  }

  document.addEventListener('click', (event) => {
    const target = event.target instanceof Element ? event.target : null;
    if (!target) return;
    const checkoutTrigger = target.closest('.sr-headless-checkout, .shiprocket-headless button, button[name="checkout"], #CartDrawer-Checkout');
    if (checkoutTrigger) {
      // Capture phase: restore the coupon BEFORE Shiprocket/Fastrr's own click
      // handler reads window.location and constructs the checkout URL.
      enforcePrepaidGuardUrl(currentEntitlement);
      ensureValidatedCouponUrl(currentEntitlement);
      reportCheckoutStart();
    }
  }, true);

  document.addEventListener('submit', (event) => {
    const form = event.target instanceof HTMLFormElement ? event.target : null;
    if (form && (form.id === 'cart' || form.id === 'CartDrawer-Form')) {
      enforcePrepaidGuardUrl(currentEntitlement);
      ensureValidatedCouponUrl(currentEntitlement);
      reportCheckoutStart();
    }
  }, true);

  window.addEventListener('pageshow', () => enforcePrepaidGuardUrl(currentEntitlement));
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'visible') enforcePrepaidGuardUrl(currentEntitlement);
  });

  window.Performer = Object.freeze({
    getOffer: () => currentEntitlement,
    getSessionKey: () => currentSessionKey,
    syncCart: syncCartAttributes,
    reportCheckoutStart,
    enforcePrepaidGuard: () => enforcePrepaidGuardUrl(currentEntitlement)
  });

  init().catch(() => { document.documentElement.classList.remove('performer-offer-pending'); });
})();
